import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Sparkles, Plus, Trash2, ArrowUpRight } from "lucide-react";

const WishBoard = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [wishes, setWishes] = useState(null);
  const [destination, setDestination] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/wishes");
      setWishes(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setWishes([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addWish = async (e) => {
    e.preventDefault();
    if (adding || !destination.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/wishes", {
        destination: destination.trim(),
        note: note.trim() || null,
      });
      setWishes((prev) => [data, ...(prev || [])]);
      setDestination("");
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/wishes/${pendingDelete}`);
      setWishes((prev) => prev.filter((w) => w.id !== pendingDelete));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPendingDelete(null);
    }
  };

  const evoke = (dest) => {
    navigate(`/plan?destination=${encodeURIComponent(dest)}`);
  };

  return (
    <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="wishes-page">
      <span className="overline">Wish Board</span>
      <h1 className="font-heading text-5xl sm:text-6xl mt-3 tracking-tighter text-[#EDE7D8]">{t.wishes.title}</h1>
      <p className="text-[#B79A5A] mt-3 max-w-xl">{t.wishes.subtitle}</p>

      <form
        onSubmit={addWish}
        className="mt-10 grid-card p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
        data-testid="wish-form"
      >
        <div className="md:col-span-4">
          <label className="field-label">{t.plan.destination}</label>
          <input
            className="field-input"
            required
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={t.wishes.destination_placeholder}
            data-testid="wish-destination"
          />
        </div>
        <div className="md:col-span-6">
          <label className="field-label">Note</label>
          <input
            className="field-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t.wishes.note_placeholder}
            data-testid="wish-note"
          />
        </div>
        <div className="md:col-span-2 flex items-end">
          <button type="submit" className="btn-pill w-full justify-center" disabled={adding} data-testid="wish-add-submit">
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            {t.wishes.add}
          </button>
        </div>
      </form>

      {wishes === null ? (
        <p className="mt-16 text-[#B79A5A]">{t.common.loading}</p>
      ) : wishes.length === 0 ? (
        <div className="mt-12 grid-card p-16 text-center" data-testid="wishes-empty">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Sparkles className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-3xl mt-8 text-[#EDE7D8]">{t.wishes.empty_title}</h3>
          <p className="text-[#B79A5A] mt-2">{t.wishes.empty_desc}</p>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wishes.map((w, i) => (
            <div
              key={w.id}
              className="grid-card p-6 fade-up flex flex-col justify-between"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`wish-card-${w.id}`}
            >
              <div>
                <div className="flex items-start justify-between">
                  <span className="overline">Sogno #{i + 1}</span>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(w.id)}
                    className="text-[#B79A5A] hover:text-[#F1D26B] p-1"
                    data-testid={`wish-delete-${w.id}`}
                    aria-label="delete wish"
                  >
                    <Trash2 className="w-4 h-4 pointer-events-none" strokeWidth={1.5} />
                  </button>
                </div>
                <h3 className="font-heading text-3xl mt-3 text-[#EDE7D8] leading-tight">{w.destination}</h3>
                {w.note ? (
                  <p className="text-sm text-[#EDE7D8]/75 mt-3 leading-relaxed">{w.note}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => evoke(w.destination)}
                className="mt-6 self-start btn-pill text-sm"
                data-testid={`wish-evoke-${w.id}`}
              >
                {t.wishes.evoke}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={t.wishes.delete_wish}
        description={t.trip.delete_confirm}
        confirmLabel={t.trip.delete_action}
        cancelLabel={t.trip.cancel}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        testId="wish-delete-dialog"
      />
    </main>
  );
};

export default WishBoard;
